import { $trl } from '@/utils/plugins/prompts';

const days = ['aw.sunday', 'aw.monday', 'aw.tuesday', 'aw.wednesday', 'aw.thursday', 'aw.friday', 'aw.saturday'];
const shortDays = ['aw.sunday.short', 'aw.monday.short', 'aw.tuesday.short', 'aw.wednesday.short', 'aw.thursday.short', 'aw.friday.short', 'aw.saturday.short'];
const letterDays = ['aw.sunday.letter', 'aw.monday.letter', 'aw.tuesday.letter', 'aw.wednesday.letter', 'aw.thursday.letter', 'aw.friday.letter', 'aw.saturday.letter'];

const months = ['aw.january', 'aw.february', 'aw.march', 'aw.april', 'aw.may', 'aw.june', 'aw.july', 'aw.august', 'aw.september', 'aw.october', 'aw.november', 'aw.december'];
const shortMonths = ['aw.january.short', 'aw.february.short', 'aw.march.short', 'aw.april.short', 'aw.may.short', 'aw.june.short', 'aw.july.short', 'aw.august.short', 'aw.september.short', 'aw.october.short', 'aw.november.short', 'aw.december.short'];

export const DATE_FORMAT = '%Y-%m-%d';
export const DATETIME_FORMAT = '%Y-%m-%dT%H:%M';
export const TIME_FORMAT = '%H:%M';

export const zeroPad = function (number, digits) {
    digits = digits || 2;
    return ((new Array(digits)).join('0') + number).slice(-digits);
};

// !!! Order matters, DESC
const FORMAT_TOKENS = {
    '%y': {
        format: (date) => date.getFullYear().toString().slice(-2),
        parse: (date, value) => date.setYear(value + 2000),
    },
    '%Y': {
        format: (date) => date.getFullYear().toString(),
        parse: (date, value) => date.setYear(value),
    },
    '%m': {
        format: (date) => zeroPad(date.getMonth() + 1, 2),
        parse: (date, value) => date.setMonth(value - 1),
    },
    '#month:3': {
        format: (date) => $trl(shortMonths[date.getMonth()]),
        parse: (date) => date,
    },
    '#month': {
        format: (date) => $trl(months[date.getMonth()]),
        parse: (date) => date,
    },
    '%d': {
        format: (date) => zeroPad(date.getDate(), 2),
        parse: (date, value) => date.setDate(value),
    },
    '%D': {
        format: (date) => date.getDate().toString(),
        parse: (date, value) => date.setDate(value),
    },
    '#day:3': {
        format: (date) => $trl(shortDays[date.getDay()]),
        parse: (date) => date,
    },
    '#day:1': {
        format: (date) => $trl(letterDays[date.getDay()]),
        parse: (date) => date,
    },
    '#day': {
        format: (date) => $trl(days[date.getDay()]),
        parse: (date) => date,
    },
    '#ampm': {
        format: (date) => (date.getHours() < 12 ? $trl('aw.am') : $trl('aw.pm')),
        parse: (date, value) => {
            if (value === $trl('aw.pm')) {
                date.setHours(date.getHours() + 12);
            } else if (date.getHours() === 12) {
                date.setHours(0); // 12 hour format use 12:00 AM to present 00:00
            }
        },
    },
    '%H': {
        format: (date) => zeroPad(date.getHours(), 2),
        parse: (date, value) => date.setHours(value),
    },
    '%h': {
        format: (date) => date.getHours() > 12 ? date.getHours() % 12 : (date.getHours() === 0 ? 12 : date.getHours()),
        parse: (date, value) => date.setHours(value),
    },
    '%M': {
        format: (date) => zeroPad(date.getMinutes(), 2),
        parse: (date, value) => date.setMinutes(value),
    },
    '%s': {
        format: (date) => date.getSeconds().toString(),
        parse: (date, value) => date.setSeconds(value),
    },
    '%S': {
        format: (date) => zeroPad(date.getSeconds(), 2),
        parse: (date, value) => date.setSeconds(value),
    },
    '%%': {
        format: () => '%',
        parse: (date) => date,
    },
};

export const str2date = function (dateString, sformat = DATE_FORMAT) {
    let date = new Date(1970, 0, 1);
    let digitsArray = dateString.match(/\d+|(am|pm)+/g);
    let handlersArray = sformat.match(new RegExp(Object.keys(FORMAT_TOKENS).join('|'), 'g'));

    Object.keys(FORMAT_TOKENS).forEach(key => {
        let index = handlersArray.indexOf(key);
        if (index !== -1) {
            FORMAT_TOKENS[key].parse(date, parseInt(digitsArray[index], 10));
        }
    });

    return date;
};

export const date2str = function (date, sformat = DATE_FORMAT) {
    for (let token in FORMAT_TOKENS) {
        if (Object.prototype.hasOwnProperty.call(FORMAT_TOKENS, token)) {
            const r = new RegExp(token, 'g');
            sformat = sformat.replace(r, function () {
                return FORMAT_TOKENS[token].format(date);
            });
        }
    }
    return sformat;
};

export const addDays = function (date, days) {
    let currentDate = date.getDate();
    let newDate = new Date(date);
    newDate.setDate(currentDate + days);
    return newDate;
};

export const dateRange = function (from, until, inclusive = false, format = DATE_FORMAT) {
    let toDate = str2date(until, format);
    let date = str2date(from, format);
    if (date.getTime() === toDate.getTime()) {
        return inclusive ? [date2str(toDate)] : [];
    }
    if (date.getTime() >= toDate.getTime()) {
        return [];
    }
    let result = [];
    const maxDate = inclusive ? addDays(toDate, 1) : toDate;
    // eslint-disable-next-line no-unmodified-loop-condition
    while (date.getTime() < maxDate.getTime()) {
        result.push(date2str(date));
        date.setDate(date.getDate() + 1);
    }

    return result;
};

export const daysInMonth = function (month, year) {
    // year fallback 2016 - last leap-year
    let date = new Date(year || 2016, parseInt(month, 10) + 1, 0);
    return date.getDate();
};

export const countMonths = function (dateString) {
    let date = str2date(dateString);
    return date.getFullYear() * 12 + date.getMonth() + 1;
};
